// App.js
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut, setPersistence, inMemoryPersistence, signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import RoleLogin from './RoleLogin';
import ResetPasswordAction from './ResetPasswordAction';
import { FeatureFlagsProvider } from './featureFlagsContext';
import { WorkOrderStatusesProvider } from './workOrderStatusesContext';
import { TagGroupsProvider } from './tagGroupsContext';
import { getUserRoles, shellForRoles, isAdminRoles, isCoordinatorRoles, isTechnicianRoles } from './roleUtils';
import './App.css';
import Button from './components/Button';
import BrandLoader from './components/BrandLoader';
import { COLORS } from './theme';
import { identityItem, setIdentityCache, clearIdentityCache } from './identityCache';

// A user only ever renders exactly one of these shells, based on their roles — lazy-loading
// them means a technician's login never downloads the office Dashboard's code (and everything
// it in turn pulls in) at all, and vice versa. Same reasoning as Dashboard.js's own per-tab
// code-splitting, applied one level higher. Dashboard covers both Admin and Coordinator
// (shellForRoles' 'office' case — SettingsHub.js itself narrows what a Coordinator sees inside
// it); everyone else gets TechDashboard.
const Dashboard = lazy(() => import('./Dashboard'));
const TechDashboard = lazy(() => import('./TechDashboard'));
const PlatformOwnerConsole = lazy(() => import('./PlatformOwnerConsole'));

function App() {
  const [user, setUser] = useState(null);
  // Role system v2 (multi-role) — always an array once resolved (never a single string), null
  // only until the very first resolution completes. See roleUtils.js's getUserRoles() for the
  // roles-array-with-legacy-role-fallback logic this and every other role read in the app shares.
  const [userRoles, setUserRoles] = useState(null);
  const [technicianName, setTechnicianName] = useState(null);
  const [companyIds, setCompanyIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Set from the signed-in user's own ID token custom claims (functions/masterAdmin.js's
  // impersonateCompany stamps these onto the custom token it mints) rather than anything
  // Firestore- or client-asserted — can't be spoofed, since the claims are baked into the
  // token Firebase itself signs.
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedByEmail, setImpersonatedByEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspendedCompany, setSuspendedCompany] = useState(null);
  // A Master Admin Console "Impersonate" click opens a NEW tab pointed here with
  // ?impersonate=<customToken> — consumed once on mount below via signInWithCustomToken under
  // inMemoryPersistence. This is NOT browserSessionPersistence — Firebase Auth's persisted
  // session storage (IndexedDB) is genuinely shared across every tab of the same origin, so
  // switching THIS tab to session/local persistence before signing in actually clears
  // whatever real session is stored for every OTHER open tab too (confirmed live: it signed
  // the platform admin's own separate tab out entirely). inMemoryPersistence never touches
  // that shared storage at all, so this tab's impersonated session is genuinely isolated — the
  // real tradeoff is that reloading THIS tab loses the impersonated session (acceptable, even
  // desirable, for a short support session; re-impersonate from the console if needed).
  // See MasterAdminConsole.js.
  const [impersonateToken, setImpersonateToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('impersonate');
  });
  // A password reset link (admin-sent or self-service "Forgot password?") now points back at
  // this app with ?mode=resetPassword&oobCode=... (see ResetPasswordAction.js) instead of
  // Firebase's generic firebaseapp.com action page. Read once on mount, not on every render —
  // this is only ever meaningful for the page load that arrived via that link.
  const [resetOobCode, setResetOobCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'resetPassword' ? params.get('oobCode') : null;
  });
  // A dual-role user (admin or coordinator PLUS technician — a "working owner" per
  // roleUtils.js's shellForRoles comment) has no way to reach TechDashboard at all otherwise:
  // shellForRoles alone always resolves office-capable roles to 'office'. This overrides that
  // resolution once the user explicitly switches. Persisted across reloads (not just this
  // session) since someone working a field shift might reload mid-shift and shouldn't be bounced
  // back to the office shell — read once on mount via the same lazy-useState pattern as
  // impersonateToken/resetOobCode above. Cleared on logout below so a later, different account on
  // the same device never inherits a stranger's view preference.
  const [shellViewOverride, setShellViewOverride] = useState(() => localStorage.getItem('shellViewOverride'));

  useEffect(() => {
    // Looks up the role/name directly from Firestore rather than trusting localStorage,
    // which RoleLogin.js also writes to — that write happens after its own async query, so
    // relying on it here raced against onAuthStateChanged firing first with a stale/empty
    // value that a long-lived TechDashboard interval would then be permanently stuck with.
    const resolveRoleAndName = async (currentUser) => {
      try {
        const userDocRef = doc(db, 'appUsers', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const roles = getUserRoles(userData);
          const techName = userData.name || '';
          // ['cpp'] only when the field is missing entirely — a real account that predates the
          // multi-company migration (or the migration script simply not having run yet).
          // Distinct from an array that's PRESENT but genuinely empty (companyDeletion.js's
          // purge cascade arrayRemoves a deleted company from every member's companyIds, and
          // someone whose only company just got purged ends up with a real, empty array) —
          // that person isn't part of cpp and shouldn't be shown its data either; see
          // ZeroCompaniesScreen below for what they see instead.
          const ids = Array.isArray(userData.companyIds) ? userData.companyIds : ['cpp'];
          // Not cached to localStorage (unlike roles/techName/ids above) — it's read fresh from
          // Firestore every time and simply defaults to false in the fallback path below, which
          // is the correct fail-closed behavior for a platform-admin-only console.
          const superAdmin = userData.superAdmin === true;
          // NOT cached during an impersonation session.
          //
          // Real bug fixed here: impersonation isolates only Firebase Auth state (inMemory
          // persistence in that tab), but localStorage is per-ORIGIN and shared by every tab. The
          // impersonation tab therefore overwrote the platform admin's own cached
          // userRoles/technicianName/companyIds with the impersonated user's — and closing it
          // (handleLogout) deleted them outright. Every other open tab then read the wrong
          // identity from the cache: WorkOrderList's isRealAdmin, CompanySplitSummary's
          // admin-only controls and internalQuoteService's own company scoping all key off
          // exactly these keys.
          // The CLAIM, read here, not the isImpersonating state — that is set from a separate
          // getIdTokenResult() call which resolves after this function has already run, so reading
          // it here would always see the previous render's value (false on the very first pass,
          // which is exactly the pass that does the damage).
          const impersonating = await currentUser.getIdTokenResult()
            .then((t) => (t.claims || {}).impersonation === true)
            .catch(() => false);
          // An impersonation tab caches to sessionStorage (per-TAB) instead of localStorage
          // (per-ORIGIN, shared by every tab). That gives the impersonated session a correct
          // identity of its own — several services scope their Firestore queries by these cached
          // company ids, and a query scoped to companies the impersonated user does not belong to
          // is not provable against the rules and gets rejected outright — while leaving what the
          // platform admin's other tabs are reading untouched. See identityCache.js.
          setIdentityCache({ roles, technicianName: techName, companyIds: ids }, { tabScoped: impersonating });
          // Archiving a user (UserManagement.js's handleArchive) was purely cosmetic until this
          // check existed — it flipped this flag and nothing anywhere read it, so a terminated
          // employee kept full working access while the admin who archived them saw "Archived".
          // Deliberately `=== false`, never `!active`: real accounts exist with no `active` field
          // at all (predating it), and treating a missing field as archived would lock out
          // legitimate users — the far worse failure. Only an explicit false locks the door.
          const deactivated = userData.active === false;
          return { roles, techName, ids, superAdmin, deactivated };
        }
        // The document genuinely does not exist — a distinct case from the read having FAILED,
        // which is what the fallback below is for. Falling through to a cached identity here
        // handed a signed-in user with no appUsers profile whatever was last cached on this
        // browser (not cleared on deactivation or forced sign-out), instead of the "profile not
        // found" message RoleLogin means to show.
        return { roles: [], techName: '', ids: [], superAdmin: false, deactivated: false, noProfile: true };
      } catch (error) {
        console.error('Error resolving user role/name:', error);
      }

      // Fall back to whatever was cached from a previous successful login rather than
      // nothing, but this should only be reached if the Firestore lookup itself failed. Empty
      // (not ['cpp']) when there's nothing cached either — this app now has real companies
      // besides cpp, so a genuine "we don't know who this is" case should show nobody's data
      // rather than guessing at cpp's.
      let cachedIds = [];
      try {
        const stored = JSON.parse(identityItem('companyIds') || 'null');
        if (Array.isArray(stored) && stored.length > 0) cachedIds = stored;
      } catch (e) {
        // ignore malformed cache, fall back to default below
      }
      let cachedRoles = ['technician'];
      try {
        const stored = JSON.parse(identityItem('userRoles') || 'null');
        if (Array.isArray(stored) && stored.length > 0) cachedRoles = stored;
      } catch (e) {
        // ignore malformed cache, fall back to default above
      }
      return {
        roles: cachedRoles,
        techName: identityItem('technicianName') || '',
        ids: cachedIds,
        superAdmin: false,
        // Fails OPEN on purpose: this path only runs when the Firestore read itself failed, and a
        // dropped connection must never be mistaken for "this account was archived." The server
        // side of archiving (setAuthUserActive disabling the Auth account + revoking refresh
        // tokens) is the real enforcement; this flag is the immediate client-side companion.
        deactivated: false
      };
    };

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        resolveRoleAndName(currentUser).then(({ roles, techName, ids, superAdmin, deactivated, noProfile }) => {
          // Signed in to Firebase Auth but with no appUsers document at all. Distinct from a
          // FAILED read (which falls back to the cache below, deliberately). Without this the
          // stale cache stood in for a profile that does not exist, and RoleLogin's own "profile
          // not found" message was never reached — onAuthStateChanged fires before
          // signInWithEmailAndPassword resolves, so App has already swapped RoleLogin for the
          // loading screen by the time RoleLogin's own lookup returns.
          if (noProfile) {
            clearIdentityCache();
            signOut(auth)
              .catch((error) => console.error('Error signing out an account with no profile:', error))
              .finally(() => {
                setLoading(false);
                alert('This login isn\'t linked to a user profile. Contact your administrator.');
              });
            return;
          }
          if (deactivated) {
            signOut(auth)
              .catch((error) => console.error('Error signing out a deactivated account:', error))
              .finally(() => {
                // Cleared too: a deactivated account's cached identity must not linger for the
                // next person to sign in on this browser.
                clearIdentityCache();
                setLoading(false);
                alert('This account has been archived. Contact your administrator if you believe this is a mistake.');
              });
            return;
          }
          setUserRoles(roles);
          setTechnicianName(techName);
          setCompanyIds(ids);
          setIsSuperAdmin(superAdmin);
          setLoading(false);
        });
        currentUser.getIdTokenResult().then((tokenResult) => {
          const claims = tokenResult.claims || {};
          setIsImpersonating(claims.impersonation === true);
          setImpersonatedByEmail(claims.impersonatedByEmail || null);
        }).catch(() => {
          setIsImpersonating(false);
          setImpersonatedByEmail(null);
        });
      } else {
        setUser(null);
        setUserRoles(null);
        setTechnicianName(null);
        setCompanyIds([]);
        setIsSuperAdmin(false);
        setIsImpersonating(false);
        setImpersonatedByEmail(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // A suspended company (functions/masterAdmin.js's suspendCompany — a platform-admin lever for
  // non-payment/ToS issues, short of the existing 30-day-grace-period deletion flow) blocks
  // every non-super-admin role behind a full-screen notice below, client-side only — same
  // soft/client-level enforcement this codebase already uses for `archived` companies
  // (companyContext.js's activeMyCompanyIds filter), not a new one-off rules-level approach.
  // `companies` is world-readable to any signed-in user, so this needs no special rule. A live
  // onSnapshot rather than a one-time getDoc deliberately — this needs to take effect for
  // someone already mid-session the moment a platform admin suspends them, not only the next
  // time they happen to log back in (confirmed live: a one-time fetch missed a suspension that
  // landed a couple seconds after this component had already resolved companyIds and rendered).
  // Super admins are never blocked — they have platform authority to reactivate/impersonate
  // regardless, and PlatformOwnerConsole's company-less path wouldn't have a companyIds[0] to
  // check here anyway.
  //
  // EVERY company the user belongs to, not just companyIds[0]. Real bug fixed here: this watched
  // one document — the primary — so anyone holding a suspended company as a SECONDARY kept full
  // access to all of its data, which is the ordinary multi-company setup this app is built
  // around. Suspension is the platform's enforcement lever for non-payment, and it was bypassed
  // by simply not having the suspended company listed first.
  //
  // The hard block below is reserved for having NOTHING left to work in — blocking the whole app
  // because one of two companies is suspended would lock a paid-up company's staff out of their
  // own data. A suspended company that is not the user's only one is instead filtered out of
  // every query by companyContext.js's activeMyCompanyIds, alongside archived companies, so its
  // data stops being reachable without taking the rest of the app down.
  useEffect(() => {
    if (companyIds.length === 0 || isSuperAdmin) { setSuspendedCompany(null); return undefined; }
    const suspendedById = {};
    const unsubscribers = companyIds.map((id) => onSnapshot(doc(db, 'companies', id), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        suspendedById[id] = data.isSuspended ? { name: data.name, reason: data.suspendedReason || null } : null;
      } else {
        suspendedById[id] = null;
      }
      // Only once every company has reported in, so a partially-loaded set can't briefly look
      // like "all suspended" and flash the block screen at someone who isn't suspended.
      const reported = companyIds.filter((cid) => cid in suspendedById);
      if (reported.length < companyIds.length) return;
      const allSuspended = companyIds.every((cid) => suspendedById[cid]);
      setSuspendedCompany(allSuspended ? suspendedById[companyIds[0]] : null);
    }, () => {}));
    return () => unsubscribers.forEach((fn) => fn());
  }, [companyIds, isSuperAdmin]);

  // Consumes ?impersonate=<customToken> exactly once, isolated to this tab (see the
  // impersonateToken state comment above) — signInWithCustomToken's success fires the
  // onAuthStateChanged listener above with the impersonated admin's own uid, which resolves
  // their real role/companyIds/superAdmin from Firestore the normal way.
  useEffect(() => {
    if (!impersonateToken) return;
    (async () => {
      try {
        await setPersistence(auth, inMemoryPersistence);
        await signInWithCustomToken(auth, impersonateToken);
      } catch (error) {
        console.error('Error starting impersonation session:', error);
      } finally {
        const params = new URLSearchParams(window.location.search);
        params.delete('impersonate');
        const newSearch = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
        setImpersonateToken(null);
      }
    })();
  }, [impersonateToken]);

  const handleLoginSuccess = (user, roles) => {
    setUser(user);
    setUserRoles(roles);
    const techName = identityItem('technicianName') || '';
    setTechnicianName(techName);
    // Empty (not ['cpp']) when nothing's cached yet — callers (RoleLogin.js's handleLogin,
    // signupService.js's signupCompany) are expected to have already written the real
    // companyIds to localStorage before calling this, same as they already do for userRoles/
    // technicianName above; falling back to cpp's real company id here would be guessing at
    // someone else's business data for any company that isn't cpp.
    let ids = [];
    try {
      const stored = JSON.parse(identityItem('companyIds') || 'null');
      if (Array.isArray(stored) && stored.length > 0) ids = stored;
    } catch (e) {
      // ignore malformed cache, fall back to default above
    }
    setCompanyIds(ids);
  };

  const handleLogout = async () => {
    try {
      // Capture BEFORE signOut clears the session — an impersonation tab must not wipe the
      // platform admin's own cached identity out of the shared, per-origin localStorage that
      // every other tab reads. Its own Auth session is in-memory and disappears with the tab.
      const wasImpersonating = isImpersonating;
      await signOut(auth);
      // An impersonation tab clears only its OWN (sessionStorage) copy — wiping the shared one is
      // precisely the bug identityCache.js exists to prevent.
      clearIdentityCache({ tabScopedOnly: wasImpersonating });
      localStorage.removeItem('shellViewOverride');
      setUser(null);
      setUserRoles(null);
      setTechnicianName(null);
      setCompanyIds([]);
      setIsSuperAdmin(false);
      setIsImpersonating(false);
      setImpersonatedByEmail(null);
      setShellViewOverride(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Checked before the loading/auth gates below — resetting a password is meant to work
  // whether or not anyone happens to already be signed in on this device/browser.
  if (resetOobCode) {
    return (
      <ResetPasswordAction
        oobCode={resetOobCode}
        onDone={() => {
          const params = new URLSearchParams(window.location.search);
          params.delete('mode');
          params.delete('oobCode');
          params.delete('apiKey');
          params.delete('lang');
          const newSearch = params.toString();
          window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
          setResetOobCode(null);
        }}
      />
    );
  }

  if (impersonateToken) {
    return <div className="loading-screen"><BrandLoader title="Signing you in…" /></div>;
  }

  if (loading) {
    return <div className="loading-screen"><BrandLoader /></div>;
  }

  if (!user) {
    return <RoleLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // `user` can become truthy (via onAuthStateChanged's own setUser, independent of and
  // possibly racing ahead of RoleLogin's handleLoginSuccess) before userRoles has resolved,
  // since resolving it is an async Firestore lookup. Without this check, the shell-selection
  // logic below runs against a null roles array during that gap, which briefly mounts the
  // office Dashboard for every technician login — confirmed via its admin-only console errors
  // firing on technician sign-in even though TechDashboard never imports the modules that
  // produce them.
  if (userRoles === null) {
    return <div className="loading-screen"><BrandLoader /></div>;
  }

  // Reachable for real now that functions/companyDeletion.js's purge cascade exists — someone
  // whose only company just got permanently deleted still has a valid login (deleting a
  // person's own account isn't part of deleting their company's data, see companyDeletion.js's
  // own comment on why), just nothing left to show them.
  // A platform super-admin doesn't need to belong to any company at all — every masterAdmin.js
  // Cloud Function they'd use authorizes purely off appUsers.superAdmin, with no companyIds
  // check. Route them to the dedicated PlatformOwnerConsole shell instead: Dashboard.js's own
  // tabs (DashboardHome, WorkOrderList, etc.) all run company-scoped Firestore 'in' queries
  // that throw outright on an empty companyIds array (confirmed live), so this bypasses that
  // whole class of crash rather than defensively patching every one of those call sites for a
  // case only this one account type ever hits.
  if (companyIds.length === 0 && isSuperAdmin) {
    return (
      <Suspense fallback={<div className="loading-screen"><BrandLoader /></div>}>
        <PlatformOwnerConsole user={user} onLogout={handleLogout} />
      </Suspense>
    );
  }

  if (companyIds.length === 0) {
    return (
      <div className="loading-screen" style={{ flexDirection: 'column', gap: '16px' }}>
        <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.text }}>You're not part of any company right now.</p>
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textMuted, maxWidth: '360px', textAlign: 'center' }}>
          If a company you belonged to was recently deleted, or you were removed from one, ask an admin to add
          you back, or sign up for a new company account.
        </p>
        <Button onClick={handleLogout}>Log Out</Button>
      </div>
    );
  }

  if (suspendedCompany) {
    return (
      <div className="loading-screen" style={{ flexDirection: 'column', gap: '16px' }}>
        <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.text }}>This account is temporarily suspended.</p>
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textMuted, maxWidth: '360px', textAlign: 'center' }}>
          {suspendedCompany.reason || `Access to ${suspendedCompany.name} has been paused. Contact support to resolve this.`}
        </p>
        <Button onClick={handleLogout}>Log Out</Button>
      </div>
    );
  }

  // Show appropriate dashboard based on role(s) — see roleUtils.js's shellForRoles for the
  // precedence (office-capable roles win over field roles). canSwitchShellView is the one
  // genuinely ambiguous case: office-capable (admin/coordinator) AND technician at once — the
  // override above only ever applies for that combination, so a single-role user can never end
  // up on the "wrong" shell no matter what's left over in localStorage from a prior account.
  // The primary company these providers scope every settings doc to — companyIds[0], the same
  // anchor featureFlagsContext.js documents why it uses specifically.
  const primaryCompanyId = companyIds && companyIds.length > 0 ? companyIds[0] : null;
  const canSwitchShellView = (isAdminRoles(userRoles) || isCoordinatorRoles(userRoles)) && isTechnicianRoles(userRoles);
  const shell = canSwitchShellView && shellViewOverride ? shellViewOverride : shellForRoles(userRoles);

  const handleSwitchShellView = (view) => {
    localStorage.setItem('shellViewOverride', view);
    setShellViewOverride(view);
  };

  return (
    <FeatureFlagsProvider companyId={primaryCompanyId}>
      <WorkOrderStatusesProvider companyId={primaryCompanyId} myCompanyIds={companyIds}>
      <TagGroupsProvider companyIds={companyIds}>
        <Suspense fallback={<div className="loading-screen"><BrandLoader /></div>}>
          {shell === 'office' ? (
            <Dashboard
              user={user}
              userRoles={userRoles}
              userName={technicianName}
              companyIds={companyIds}
              onLogout={handleLogout}
              isSuperAdmin={isSuperAdmin}
              isImpersonating={isImpersonating}
              impersonatedByEmail={impersonatedByEmail}
              canSwitchShellView={canSwitchShellView}
              onSwitchShellView={handleSwitchShellView}
            />
          ) : (
            <TechDashboard
              user={user}
              technicianName={technicianName}
              companyIds={companyIds}
              onLogout={handleLogout}
              canSwitchShellView={canSwitchShellView}
              onSwitchShellView={handleSwitchShellView}
            />
          )}
        </Suspense>
      </TagGroupsProvider>
      </WorkOrderStatusesProvider>
    </FeatureFlagsProvider>
  );
}

export default App;